/**
 * audit-grouping — group consecutive audit entries by actor + a 5-second window
 * (86-followup #4). A bulk import writes a hauler_wells row AND a hauler_tanks row
 * at the same instant; without grouping they render as two near-identical rows that
 * look like duplicates. Pure + RN-free so it can be unit-tested without rendering.
 */
import type { AuditEntry } from './hooks';

export interface AuditGroupT {
  readonly items: readonly AuditEntry[];
}

/** ms since epoch for an ISO timestamp; 0 on parse failure. */
export function auditMs(iso: string): number {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Group entries that share an actor (name + role) AND fall within 5 seconds of the
 * group's first (anchor) entry. Entries arrive newest-first; order is preserved and
 * only CONSECUTIVE same-actor entries merge, so a later unrelated event never folds
 * into an earlier group.
 */
export function groupAudit(entries: readonly AuditEntry[]): AuditGroupT[] {
  const groups: AuditEntry[][] = [];
  for (const e of entries) {
    const cur = groups[groups.length - 1];
    const head = cur?.[0];
    if (
      cur !== undefined &&
      head !== undefined &&
      head.changedByName === e.changedByName &&
      head.changedByRole === e.changedByRole &&
      Math.abs(auditMs(head.changedAt) - auditMs(e.changedAt)) <= 5000
    ) {
      cur.push(e);
    } else {
      groups.push([e]);
    }
  }
  return groups.map((items) => ({ items }));
}
